import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Rss, Users, Heart, TrendingUp, Megaphone } from 'lucide-react';
import { UserFeed } from '@/components/feed/UserFeed';
import { AdminFeedPosts } from '@/components/admin/AdminFeedPosts';
import { AdminPostToFeed } from '@/components/admin/AdminPostToFeed';
import { useUserRole } from '@/hooks/useUserRole';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/useIsMobile';

const Feed: React.FC = () => {
  const navigate = useNavigate();
  const { userId, permissions } = useUserRole();
  const [adminPostRefresh, setAdminPostRefresh] = useState(0);
  const isMobile = useIsMobile();

  const handleAdminPostCreated = () => {
    setAdminPostRefresh(prev => prev + 1);
  };

  if (!userId) {
    return (
      <div className="container mx-auto p-6">
        <Card className="bg-card border-border">
          <CardContent className="p-6 text-center">
            <Rss className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Please Log In</h2>
            <p className="text-muted-foreground">
              You need to be logged in to view your activity feed
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <>
      {isMobile ? <MobileHeader /> : <Header />}
      <div className="min-h-screen bg-background pt-20 pb-20"> {/* Added pb-20 for mobile bottom nav */}
        <main className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Rss className="h-6 w-6 text-primary" />
              </div>
              Activity Feed
            </h1>
            <p className="text-muted-foreground mt-2">
              Stay updated with activities from people you follow
            </p>
          </div>
          
          {/* Feed Stats */}
          <div className="flex items-center space-x-4">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                // Filter for trending content - posts with high engagement
                window.location.href = '/feed?trending=true';
              }}
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              Trending
            </Button>
          </div>
        </div>
      </div>

      {/* Feed Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Feed */}
        <div className="lg:col-span-3 space-y-6">
          {/* Admin Posts Section - Always visible */}
          <div>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-primary" />
              Official Announcements
            </h2>
            <AdminFeedPosts refreshTrigger={adminPostRefresh} />
          </div>

          {/* Admin Post Creation - Only for Admin */}
          {permissions.role === 'admin' && (
            <div>
              <Tabs defaultValue="feed" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="feed">View Feed</TabsTrigger>
                  <TabsTrigger value="post">Post to Feed</TabsTrigger>
                </TabsList>
                <TabsContent value="feed">
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Switch to "Post to Feed" tab to create admin announcements</p>
                  </div>
                </TabsContent>
                <TabsContent value="post">
                  <AdminPostToFeed onPostCreated={handleAdminPostCreated} />
                </TabsContent>
              </Tabs>
            </div>
          )}

          {/* Regular User Feed - Hidden for Admin */}
          {permissions.role !== 'admin' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Your Feed</h2>
              <UserFeed 
                userId={userId} 
                userType="supabase"
              />
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Feed Tips */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Heart className="h-4 w-4 text-red-400" />
                Feed Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-sm text-muted-foreground">
                <p className="mb-2">
                  • Follow players, coaches, venues, and other users to see their activities
                </p>
                <p className="mb-2">
                  • Like and comment on activities to engage with the community
                </p>
                <p className="mb-2">
                  • Stay updated on game results, achievements, team changes, and more
                </p>
                <p className="mb-2">
                  • Share interesting activities with your network
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-sm font-medium">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-start"
                onClick={() => navigate('/find-people')}
              >
                <Users className="h-4 w-4 mr-2" />
                Find People
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                className="w-full justify-start"
                onClick={() => navigate('/manage-following')}
              >
                <Heart className="h-4 w-4 mr-2" />
                Manage Following
              </Button>
            </CardContent>
          </Card>
        </div>
        </div>
        </main>
      </div>
      <div className="hidden md:block">
        <Footer />
      </div>
      <div className="md:hidden">
        <MobileBottomNav />
      </div>
    </>
  );
};

export default Feed;